import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { SpinnerGap, CaretRight, CaretDown, CaretLeft } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const TYPE_ORDER = { CS: 0, CT: 1, PR: 2, AF: 3, CF: 4 };
const TYPE_STYLES = {
  CF: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Cliente' },
  CS: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Consultor' },
  CT: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Consultor' },
  AF: { bg: 'bg-green-100', text: 'text-green-700', label: 'Afiliado' },
  PR: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Embaixador' },
};
const PAGE_SIZE = 30;

function sortNodes(nodes) {
  return [...nodes].sort((a, b) => {
    const oa = TYPE_ORDER[a.ID_TipoCliente] ?? 9;
    const ob = TYPE_ORDER[b.ID_TipoCliente] ?? 9;
    if (oa !== ob) return oa - ob;
    return (a.NomeCompleto || '').localeCompare(b.NomeCompleto || '', 'pt');
  });
}

function NodeRow({ node, depth, isExpanded, isLoading, hasChildren, onExpand, onSelect }) {
  const style = TYPE_STYLES[node.ID_TipoCliente] || TYPE_STYLES.CF;
  return (
    <div className="flex items-center gap-2 py-2 px-3 rounded-md hover:bg-[#F3F4F6] transition-colors" style={{ paddingLeft: `${depth * 24 + 12}px` }}>
      <button onClick={() => hasChildren && onExpand(node.ID_Cliente)}
        className={`w-5 h-5 flex items-center justify-center rounded hover:bg-[#E5E7EB] flex-shrink-0 ${hasChildren ? 'cursor-pointer' : 'invisible'}`}>
        {isLoading ? <SpinnerGap size={12} className="animate-spin text-[#0F1829]" /> : isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
      </button>
      {depth > 0 && <div className="w-3 h-px bg-[#D1D5DB]" />}
      <div className={`w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${style.bg} ${style.text}`}>
        {(node.NomeCompleto || '?').charAt(0)}
      </div>
      <button onClick={() => onSelect(node.ID_Cliente)} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#111827] truncate"><PersonLink id={node.ID_Cliente} name={node.NomeCompleto} /></span>
          <span className={`text-[7px] uppercase tracking-[0.15em] font-bold px-1 py-0.5 rounded ${style.bg} ${style.text}`}>{style.label}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-[#6B7280]">
          {node.Email && <span>{node.Email}</span>}
          {node.NumFilhos > 0 && <span>{node.NumFilhos} downline</span>}
          {node.NumEncomendas > 0 && <span>{node.NumEncomendas} enc.</span>}
        </div>
      </button>
    </div>
  );
}

function DetailPanel({ clientId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  useEffect(() => {
    setLoading(true);
    api.post('/rede/clienteDetail', { ID_Cliente: clientId }).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="bg-white border border-[#E5E7EB] rounded-lg p-5"><SpinnerGap size={20} className="animate-spin text-[#0F1829]" /></div>;
  if (!data?.cliente) return null;
  const c = data.cliente;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#0F1829] flex items-center justify-center text-white font-bold">{(c.NomeCompleto||'?').charAt(0)}</div>
          <div><h3 className="text-sm font-semibold"><PersonLink id={c.ID_Cliente} name={c.NomeCompleto} /></h3><p className="text-xs text-[#6B7280]">{c.TipoCliente} · ID: {c.ID_Cliente}</p></div>
        </div>
        <button onClick={onClose} className="text-xs text-[#0F1829] hover:underline">Fechar</button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div><span className="text-[#6B7280]">Email:</span> <span>{c.Email||'-'}</span></div>
        <div><span className="text-[#6B7280]">Empresa:</span> <span>{c.Empresa||'-'}</span></div>
        <div><span className="text-[#6B7280]">NIF:</span> <span className="font-mono">{c.NIF||'-'}</span></div>
        <div><span className="text-[#6B7280]">Local:</span> <span>{c.Localidade||'-'}</span></div>
        <div><span className="text-[#6B7280]">Registo:</span> <span>{c.DataRegisto||'-'}</span></div>
        <div><span className="text-[#6B7280]">Nasc.:</span> <span>{c.DataNascimento||'-'}</span></div>
      </div>
      {(data.contactos||[]).length > 0 && <div className="mb-3">{data.contactos.map((ct,i) => <div key={`ct-${ct.TipoContacto || 'c'}-${ct.Descricao || i}`} className="text-xs text-[#6B7280]">{ct.TipoContacto}: <span className="text-[#111827]">{ct.Descricao}</span></div>)}</div>}
      {(data.encomendas||[]).length > 0 && <div>{data.encomendas.map((e,i) => <div key={`enc-${e.ID_Encomenda || i}`} className="flex justify-between text-xs border-b border-[#F3F4F6] py-1"><span><a href={`/encomendas/${e.ID_Encomenda}`} className="text-[#0F1829] underline hover:text-[#001f7a]">#{e.ID_Encomenda}</a> · {e.Estado}</span><span className="font-mono">{fmt(e.PrecoFinal)}</span></div>)}</div>}
    </div>
  );
}

export default function TreePage() {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [childrenMap, setChildrenMap] = useState({});
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.post('/rede/arvore', {}).then(r => {
      const children = sortNodes(r.data?.children || []);
      setChildrenMap(prev => ({ ...prev, root: children }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleExpand = useCallback(async (id) => {
    if (expandedIds.has(id)) {
      setExpandedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      return;
    }
    setExpandedIds(prev => new Set([...prev, id]));
    if (childrenMap[id]) return;
    setLoadingIds(prev => new Set([...prev, id]));
    try {
      const r = await api.post('/rede/arvore', { parentId: id });
      setChildrenMap(prev => ({ ...prev, [id]: sortNodes(r.data?.children || []) }));
    } catch { setChildrenMap(prev => ({ ...prev, [id]: [] })); }
    setLoadingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  }, [expandedIds, childrenMap]);

  const buildFlat = useCallback((parentKey, depth) => {
    const children = childrenMap[parentKey] || [];
    const result = [];
    for (const child of children) {
      const isExpanded = expandedIds.has(child.ID_Cliente);
      result.push({ node: child, depth, isExpanded, isLoading: loadingIds.has(child.ID_Cliente) });
      if (isExpanded && childrenMap[child.ID_Cliente]) {
        result.push(...buildFlat(child.ID_Cliente, depth + 1));
      }
    }
    return result;
  }, [childrenMap, expandedIds, loadingIds]);

  const flat = buildFlat('root', 0);
  const totalPages = Math.max(1, Math.ceil(flat.length / PAGE_SIZE));
  const paged = flat.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { if (page > totalPages) setPage(1); }, [page, totalPages]);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  return (
    <div data-testid="tree-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Rede - Árvore</h1>
        <p className="text-sm text-[#6B7280] mt-1">Clique nas setas para expandir. Clique no nome para ver detalhes.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="network-tree">
          <p className="text-xs text-[#6B7280] mb-3">{(childrenMap.root||[]).length} membros diretos · {flat.length} visíveis</p>
          <div className="space-y-0.5">
            {paged.map((item, idx) => (
              <NodeRow key={item.node.ID_Cliente + '-' + idx} node={item.node} depth={item.depth} isExpanded={item.isExpanded}
                isLoading={item.isLoading} hasChildren={item.node.NumFilhos > 0} onExpand={handleExpand} onSelect={setSelected} />
            ))}
          </div>
          {flat.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem membros na rede</div>}
          {totalPages > 1 && (
            <div className="flex flex-col items-center gap-2 mt-4 pt-3 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({flat.length} registos)</p>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  <CaretLeft size={12} /> Anterior
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
                      {p}
                    </button>
                  );
                })}
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  Seguinte <CaretRight size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
        <div>{selected ? <DetailPanel clientId={selected} onClose={() => setSelected(null)} /> : <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 text-center text-[#6B7280] text-sm">Selecione um membro</div>}</div>
      </div>
    </div>
  );
}
